import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withInMemoryScrolling } from '@angular/router';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { httpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { tokenInterceptor } from './core/interceptors/token.interceptor';
import { AuthEffects } from './features/auth/store/auth.effects';
import { LocalStorageService } from './shared/localstorage.service';
import { DateLocaleService } from './shared/services/date-locale.service';
import { SharedEffects } from './shared/store/shared.effects';
import { sharedReducer } from './shared/store/shared.reducers';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled', // scrolls to top on navigation
        anchorScrolling: 'enabled', // scrolls to anchors when using #id links
      })
    ),
    provideAnimations(),
    // Catalogs, orders and quotes are registered on the lazy shell (pages/authenticated.routes.ts).
    provideStore({ shared: sharedReducer }),
    provideEffects(AuthEffects, SharedEffects),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: environment.production,
      connectInZone: true,
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideMomentDateAdapter(undefined, { useUtc: true }),
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (dateLocaleService: DateLocaleService) => dateLocaleService.getCurrentLocale(),
      deps: [DateLocaleService],
    },
    LocalStorageService,
    // CDK 21 renders overlays as top-layer popovers; keep them in the overlay container as before.
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    provideHttpClient(withXhr(), withInterceptors([httpErrorInterceptor, tokenInterceptor])),
    provideTranslateService({
      loader: provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json', failOnError: true }),
    }),
  ],
};
